import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { getAllJobs } from "@/api/misc";
import { Job } from "@/types/job";

interface JobsState {
  allJobs: Job[];
}

const initialState: JobsState = {
  allJobs: [],
};

// Async thunk for getting jobs
export const getJobsAction = createAsyncThunk(
  "job/getJobs",
  async (organizationData: { id: string }, { rejectWithValue }) => {
    try {
      const response = await getAllJobs(organizationData.id);
      // The endpoint answers `{ jobs: [...] }`. Storing that envelope put a
      // plain object behind an `allJobs: Job[]` type, so consumers that called
      // array methods on it blew up. Unwrap it, but tolerate a bare array —
      // same shape-agnostic handling as `getMeetingsAction`.
      return Array.isArray(response) ? (response as Job[]) : (response?.jobs ?? []);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const jobSlice = createSlice({
  name: "job",
  initialState,
  reducers: {
    addJob: (state, action: PayloadAction<Job>) => {
      state.allJobs.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJobsAction.fulfilled, (state, action: PayloadAction<Job[]>) => {
      // Keep the declared type honest whatever the endpoint returns.
      state.allJobs = Array.isArray(action.payload) ? action.payload : [];
    });
    // Handle other states like pending, rejected if needed
  },
});

export const { addJob } = jobSlice.actions;

export default jobSlice.reducer;
